import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, RefreshCw, Clock, ArrowRight } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { conversationsAPI } from '../../lib/api';

export default function ConversationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadConversations();
  }, [user, navigate]);

  const loadConversations = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await conversationsAPI.list();
      setConversations(res.data.items || []);
    } catch (err) {
      console.error('Erreur chargement conversations', err);
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status) => {
    const normalized = (status || '').toUpperCase();
    const map = {
      OPEN: { label: 'Ouverte', variant: 'default' },
      ASSIGNED: { label: 'En cours', variant: 'secondary' },
      CLOSED: { label: 'Clôturée', variant: 'outline' }
    };
    const cfg = map[normalized] || map.OPEN;
    return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
  };

  const handleCreate = async () => {
    if (!subject.trim() || !message.trim() || creating) return;
    setCreating(true);
    try {
      const res = await conversationsAPI.create({ subject: subject.trim(), initial_message: message.trim() });
      setSubject('');
      setMessage('');
      setConversations((prev) => [res.data, ...prev]);
    } catch (err) {
      console.error('Erreur création conversation', err);
    } finally {
      setCreating(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <StoreLayout>
      <section className="py-10 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold">Mes messages</h1>
            <p className="text-muted-foreground">Discutez avec un conseiller ou le support.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={loadConversations} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>

        {/* Nouvelle conversation */}
        <Card>
          <CardHeader>
            <CardTitle>Démarrer une conversation</CardTitle>
            <CardDescription>Expliquez votre besoin, un conseiller vous répondra.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="Sujet"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={creating}
            />
            <Textarea
              placeholder="Votre message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              disabled={creating}
            />
            <div className="flex justify-end">
              <Button onClick={handleCreate} disabled={creating || !subject.trim() || !message.trim()}>
                <Plus className="h-4 w-4 mr-2" /> Envoyer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Liste */}
        <Card>
          <CardHeader>
            <CardTitle>Conversations</CardTitle>
            <CardDescription>Historique de vos échanges</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                Chargement...
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-muted-foreground py-10 text-center">
                Aucune conversation pour le moment.
              </div>
            ) : (
              <div className="space-y-3">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className="flex items-start gap-3 p-4 border rounded-lg hover:bg-muted/50 transition cursor-pointer"
                    onClick={() => navigate(`/account/messages/${conv.id}`)}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{conv.subject || 'Conversation'}</span>
                        {statusBadge(conv.status)}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{conv.last_message}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Clock className="h-3 w-3" />
                        {conv.last_message_at ? new Date(conv.last_message_at).toLocaleString('fr-FR') : ''}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </StoreLayout>
  );
}
